package top.wcpe.allincore.dsl.compiler.codegen.internal

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer

/**
 * D2-c：把含 `await net.request(...)` 的语句序列变换为类型化状态机。
 *
 * <p>相对 D2-b，本版支持控制流内 await（if/while/for/when 体内），并把 break / continue /
 * return 翻译为跨状态跳转：</p>
 * <ul>
 *   <li>顶层顺序：遇 await 切状态，前缀作为 sync 段、await 自身作 Transition terminator。</li>
 *   <li>if 体内含 await：当前块以 Branch(cond, then, else) terminator 收尾，两支独立 lowering，
 *       通过 merge 状态汇合。</li>
 *   <li>while 体内含 await：拆 header / body / exit 三个状态；header = Branch(cond, body, exit)，
 *       body 末尾 Goto(header) 形成循环。break = Goto(exit)，continue = Goto(header)。</li>
 *   <li>for-in 体内含 await：语法糖展开为 while + 索引变量，再走 while 路径。</li>
 *   <li>when 体内含 await：对每个 branch 求值后用 Branch terminator 串成"if-else 链"。</li>
 *   <li>return 任意位置：以 ReturnT 终结当前块（runtime 抛 ReturnSignal）。</li>
 * </ul>
 *
 * <p>try / catch / finally 体内 await 在 parser 阶段已被 [DslError.E601_AWAIT_IN_TRY] 拒绝，
 * 本类不会再见到。</p>
 */
internal object StateMachineLowerer {

    /** 整个 lowering 的产出：状态机 IR + 是否真正含 await。 */
    internal data class LowerResult(
        val recordSchema: LinkedHashMap<String, IrType>,
        val entryStateId: Int,
        val blocks: List<BlockIr>,
    ) {
        /** true 表示 body 中存在 await 或 delay（任意位置），需要写状态机 wire；false 退化为普通 stmt 列表。 */
        val hasAwait: Boolean get() = blocks.any { it.terminator is Terminator.Transition || it.terminator is Terminator.Delay }
    }

    internal sealed class Terminator {
        /** 自然完成（仅出现在尾块）。 */
        object Natural : Terminator()

        /** return [value]（value 可空表示裸 return）。 */
        data class ReturnT(val value: ExprAst?) : Terminator()

        /**
         * await transition：切换状态；三个 next 各自独立（success/failure/timeout 可不同）。
         *
         * <p>两种形态以 [suspendCallee] 是否非 null 区分：</p>
         * <ul>
         *   <li>[suspendCallee] == null：旧 net.request 形态，[await] 非 null，[tailExpr] 可空。</li>
         *   <li>[suspendCallee] != null：M4 A2 Stage 3 suspend lambda 形态，[await] 为 null，
         *       [tailExpr] 强制为 null；调用 [suspendCallee]([suspendArgs]) 后把返回值绑到 [bindVarName]。</li>
         * </ul>
         */
        data class Transition(
            val await: ExprAst.NetRequestCall?,
            val bindVarName: String?,
            val tailExpr: ExprAst?,
            val successNextStateId: Int,
            val failureNextStateId: Int,
            val timeoutNextStateId: Int,
            val suspendCallee: ExprAst? = null,
            val suspendArgs: List<ExprAst> = emptyList(),
        ) : Terminator()

        /** 无条件跳转（控制流非 await 分支接合并点 / while body 回 header）。 */
        data class Goto(val nextStateId: Int) : Terminator()

        /** 条件跳转（控制流含 await 时把判定上提到 terminator）。 */
        data class Branch(
            val cond: ExprAst,
            val thenStateId: Int,
            val elseStateId: Int,
        ) : Terminator()

        /** delay(ms) 暂停：延迟 [delayMs] 毫秒后跳到 [nextStateId]。 */
        data class Delay(
            val delayMs: ExprAst,
            val nextStateId: Int,
        ) : Terminator()
    }

    internal data class BlockIr(
        val blockId: Int,
        val stmts: List<StmtAst>,
        val terminator: Terminator,
    )

    /** 主入口。 */
    fun lower(stmts: List<StmtAst>): LowerResult {
        // 1) 顶层无 await 时直接走快捷路径，避免引入状态机包装。
        if (!containsAwaitAnywhere(stmts)) {
            return LowerResult(LinkedHashMap(), 0, listOf(BlockIr(0, stmts, Terminator.Natural)))
        }
        // 2) 全树类型推断（含嵌套）。
        val types = TypeInferencer.infer(stmts)
        val builder = Builder(types)
        val entryId = builder.newId()
        builder.lowerSeq(stmts, entryId, EXIT_NATURAL)
        return LowerResult(builder.schema, entryId, builder.finished())
    }

    /** EXIT_NATURAL：序列耗尽时表示自然完成（终态）。 */
    private const val EXIT_NATURAL: Int = -1

    /** 单个循环上下文。break → exitId；continue → continueTargetId（while 与 header 同；for-in 指向 inc 块）。 */
    private data class LoopCtx(val headerId: Int, val continueTargetId: Int, val exitId: Int)

    private class Builder(private val types: Map<String, IrType>) {
        val schema = LinkedHashMap<String, IrType>()
        private val blocks = ArrayList<BlockIr?>()
        private val loopStack: ArrayDeque<LoopCtx> = ArrayDeque()

        fun newId(): Int {
            blocks.add(null)
            return blocks.size - 1
        }

        private fun finish(id: Int, stmts: List<StmtAst>, terminator: Terminator) {
            check(blocks[id] == null) { "block $id 已完成，不允许重复 finish" }
            blocks[id] = BlockIr(id, stmts, terminator)
        }

        /**
         * 收尾：未完成的 block（如两支分支都 return 导致 merge 不可达）以 Natural 兜底。
         * 不可达块对执行无影响，但保证 wire 格式中每个 id 都对应一个有效 block。
         */
        fun finished(): List<BlockIr> = blocks.mapIndexed { id, b ->
            b ?: BlockIr(id, emptyList(), Terminator.Natural)
        }

        /**
         * 主递归：把 [stmts] 在当前 [startId] 起按 await 边界切块；
         * 序列耗尽时跳到 [exitId]（-1 表示自然完成）。
         *
         * 主循环按"终结控制流 → 顶层 await → 普通累积 → 含 await 控制流"四象限分派；
         * 含 await 的控制流形态再下分发到 lowerIf/lowerWhile/lowerForIn/lowerWhen。
         */
        fun lowerSeq(stmts: List<StmtAst>, startId: Int, exitId: Int) {
            val state = SeqState(cur = startId)
            val queue: ArrayDeque<StmtAst> = ArrayDeque(stmts)

            while (queue.isNotEmpty()) {
                val s = queue.removeFirst()
                if (handleTerminalControlFlow(s, state)) return
                if (handleTopLevelAwait(s, state)) continue
                if (handleTopLevelDelay(s, state)) continue
                if (accumulateIfNoAwait(s, state)) continue
                dispatchControlFlowWithAwait(s, state)
            }

            // 序列耗尽：当前块以 Natural 或 Goto(exitId) 收尾
            if (exitId == EXIT_NATURAL) {
                finish(state.cur, state.acc.toList(), Terminator.Natural)
            } else {
                finish(state.cur, state.acc.toList(), Terminator.Goto(exitId))
            }
        }

        /** 单次 lowerSeq 调用内的可变状态。 */
        private class SeqState(var cur: Int, val acc: ArrayList<StmtAst> = ArrayList())

        /** 处理终结性控制流（return / break / continue）；命中即结束当前 lowerSeq。 */
        private fun handleTerminalControlFlow(s: StmtAst, state: SeqState): Boolean {
            if (s is StmtAst.ReturnStmt) {
                finish(state.cur, state.acc.toList(), Terminator.ReturnT(s.value))
                return true
            }
            if (s is StmtAst.BreakStmt) {
                val loop = loopStack.firstOrNull()
                    ?: error("break 不在循环上下文（pos=${s.position}）")
                finish(state.cur, state.acc.toList(), Terminator.Goto(loop.exitId))
                return true
            }
            if (s is StmtAst.ContinueStmt) {
                val loop = loopStack.firstOrNull()
                    ?: error("continue 不在循环上下文（pos=${s.position}）")
                finish(state.cur, state.acc.toList(), Terminator.Goto(loop.continueTargetId))
                return true
            }
            return false
        }

        /** 顶层 await（裸 / var x =）：finish 当前块为 Transition，新建 next 块。 */
        private fun handleTopLevelAwait(s: StmtAst, state: SeqState): Boolean {
            val awaitInfo = extractTopLevelAwait(s) ?: return false
            val nextId = newId()
            if (awaitInfo.bindName != null) {
                schema.getOrPut(awaitInfo.bindName) { typeOf(awaitInfo.bindName) }
            }
            val terminator = if (awaitInfo.suspendCall != null) {
                // suspend lambda / 通用 await 形态：把整个 suspendCall 表达式当作 callee，args 为空。
                // runtime 在状态机求值时直接对 callee 求值即可：CallExpr 本就是 callee+args 拆开的，
                // MemberCall 也整体作为 callee，求值后 invokeMemberCall 返回 PendingAwaitable / 普通值
                // 由 resumeWithAwaitableAwareness 统一识别 → await 自动等待。
                val (calleeExpr, callArgs) = when (val sc = awaitInfo.suspendCall) {
                    is ExprAst.CallExpr -> Pair(sc.callee, sc.args)
                    is ExprAst.MemberCall -> Pair(sc as ExprAst, emptyList())
                    else -> error("AwaitExpr.suspendCall 必须是 CallExpr / MemberCall，实际：${sc::class.simpleName}")
                }
                Terminator.Transition(
                    await = null,
                    bindVarName = awaitInfo.bindName,
                    tailExpr = null,
                    successNextStateId = nextId,
                    failureNextStateId = nextId,
                    timeoutNextStateId = nextId,
                    suspendCallee = calleeExpr,
                    suspendArgs = callArgs,
                )
            } else {
                Terminator.Transition(
                    await = awaitInfo.netRequest!!,
                    bindVarName = awaitInfo.bindName,
                    tailExpr = awaitInfo.tailExpr,
                    successNextStateId = nextId,
                    failureNextStateId = nextId,
                    timeoutNextStateId = nextId,
                )
            }
            finish(state.cur, state.acc.toList(), terminator)
            state.cur = nextId
            state.acc.clear()
            return true
        }

        /** 顶层 delay(ms)：finish 当前块为 Delay terminator，新建 next 块。 */
        private fun handleTopLevelDelay(s: StmtAst, state: SeqState): Boolean {
            val delayMs = extractTopLevelDelay(s) ?: return false
            val nextId = newId()
            finish(state.cur, state.acc.toList(), Terminator.Delay(delayMs, nextId))
            state.cur = nextId
            state.acc.clear()
            return true
        }

        /** 不含 await、也不含 break/continue/return：直接累积；遇 VarDecl 顺便登记 schema。 */
        private fun accumulateIfNoAwait(s: StmtAst, state: SeqState): Boolean {
            if (containsAwait(s) || containsControlEscape(s)) return false
            if (s is StmtAst.VarDeclStmt) {
                schema.getOrPut(s.name) { s.inferredType ?: typeOf(s.name) }
            }
            state.acc.add(s)
            return true
        }

        /** 含 await 的控制流：分发到各形态 lowering。 */
        private fun dispatchControlFlowWithAwait(s: StmtAst, state: SeqState) {
            when (s) {
                is StmtAst.IfStmt -> lowerIfWithAwait(s, state)
                is StmtAst.WhileStmt -> lowerWhileWithAwait(s, state)
                is StmtAst.ForInStmt -> lowerForInWithAwait(s, state)
                is StmtAst.WhenStmt -> lowerWhenWithAwait(s, state)
                is StmtAst.TryStmt -> error("try/catch 体内不允许 await（应由 parser 拦截，pos=${s.position}）")
                else -> error("不支持的含 await 语句形态：${s::class.simpleName}（pos=${s.position}）")
            }
        }

        private fun lowerIfWithAwait(s: StmtAst.IfStmt, state: SeqState) {
            val thenId = newId()
            val elseId = newId()
            val mergeId = newId()
            finish(state.cur, state.acc.toList(), Terminator.Branch(s.cond, thenId, elseId))
            lowerSeq(s.thenBody, thenId, mergeId)
            // else 即便为空也要 finish，保证可达性
            lowerSeq(s.elseBody, elseId, mergeId)
            state.cur = mergeId
            state.acc.clear()
        }

        private fun lowerWhileWithAwait(s: StmtAst.WhileStmt, state: SeqState) {
            val headerId = newId()
            val bodyId = newId()
            val exitWhileId = newId()
            finish(state.cur, state.acc.toList(), Terminator.Goto(headerId))
            finish(headerId, emptyList(), Terminator.Branch(s.cond, bodyId, exitWhileId))
            loopStack.addFirst(LoopCtx(headerId, headerId, exitWhileId))
            lowerSeq(s.body, bodyId, headerId)
            loopStack.removeFirst()
            state.cur = exitWhileId
            state.acc.clear()
        }

        /**
         * for-in 直接 lowering 为状态机：保证 continue 跳到 inc 块（而非 header），
         * 否则 body 内 await 后的 continue 会回 header 重读同一个元素，死循环。
         *
         * 状态拓扑：cur → header → body → ...(用户 body) → inc → header → exit
         */
        private fun lowerForInWithAwait(s: StmtAst.ForInStmt, state: SeqState) {
            val srcName = freshTempName("__forSrc")
            val idxName = freshTempName("__forIdx")
            schema[srcName] = IrType.ListT()
            schema[idxName] = IrType.NumberT
            schema[s.varName] = IrType.AnyT
            val pos = s.position
            state.acc.add(StmtAst.VarDeclStmt(srcName, s.iterable, pos))
            state.acc.add(StmtAst.VarDeclStmt(idxName, ExprAst.Literal.NumberLit(0.0, pos), pos))
            val headerId = newId()
            val bodyId = newId()
            val incId = newId()
            val exitId = newId()
            finish(state.cur, state.acc.toList(), Terminator.Goto(headerId))
            val cond = ExprAst.BinaryOp(
                "<",
                ExprAst.LocalRef(idxName, pos),
                ExprAst.MemberCall(ExprAst.LocalRef(srcName, pos), "size", emptyList(), pos),
                pos,
            )
            finish(headerId, emptyList(), Terminator.Branch(cond, bodyId, exitId))
            val itemDecl = StmtAst.VarDeclStmt(
                s.varName,
                ExprAst.IndexExpr(
                    ExprAst.LocalRef(srcName, pos),
                    ExprAst.LocalRef(idxName, pos),
                    pos,
                ),
                pos,
            )
            loopStack.addFirst(LoopCtx(headerId, incId, exitId))
            lowerSeq(listOf<StmtAst>(itemDecl) + s.body, bodyId, incId)
            loopStack.removeFirst()
            val incStmt = StmtAst.AssignStmt(
                LValueAst.LocalLValue(idxName, pos),
                ExprAst.BinaryOp(
                    "+",
                    ExprAst.LocalRef(idxName, pos),
                    ExprAst.Literal.NumberLit(1.0, pos),
                    pos,
                ),
                pos,
            )
            finish(incId, listOf(incStmt), Terminator.Goto(headerId))
            state.cur = exitId
            state.acc.clear()
        }

        /**
         * when 含 await：与 if 串联同构。先把 subject 求值到临时 var（避免副作用多次触发），
         * 每个 branch 一个 Branch terminator + thenId；最后一支接 else（或空 else）。
         */
        private fun lowerWhenWithAwait(s: StmtAst.WhenStmt, state: SeqState) {
            val mergeId = newId()
            val subjName = freshTempName("__whenSubj")
            schema[subjName] = IrType.AnyT
            state.acc.add(StmtAst.VarDeclStmt(subjName, s.whenExpr.subject, s.position))
            var currentDecisionId = state.cur
            var currentDecisionAcc = state.acc.toList()
            for (br in s.whenExpr.branches) {
                val cond = combineWhenBranchCond(subjName, br.values, s.position)
                val thenId = newId()
                val nextDecisionId = newId()
                finish(currentDecisionId, currentDecisionAcc, Terminator.Branch(cond, thenId, nextDecisionId))
                lowerSeq(br.body, thenId, mergeId)
                currentDecisionId = nextDecisionId
                currentDecisionAcc = emptyList()
            }
            val elseBody = s.whenExpr.elseBody ?: emptyList()
            lowerSeq(elseBody, currentDecisionId, mergeId)
            state.cur = mergeId
            state.acc.clear()
        }

        /** 取 var 名对应的推断类型，缺省 [IrType.AnyT]。 */
        private fun typeOf(name: String): IrType = types[name] ?: IrType.AnyT

        /** 构造 when 分支条件：`subj == v1 || subj == v2 || ...`；单值时不加 ||。 */
        private fun combineWhenBranchCond(
            subjName: String,
            values: List<ExprAst>,
            pos: SourcePosition,
        ): ExprAst {
            require(values.isNotEmpty()) { "when 分支必须有至少一个 value（pos=$pos）" }
            var acc: ExprAst = ExprAst.BinaryOp(
                "==",
                ExprAst.LocalRef(subjName, pos),
                values[0],
                pos,
            )
            for (i in 1 until values.size) {
                acc = ExprAst.BinaryOp(
                    "||",
                    acc,
                    ExprAst.BinaryOp("==", ExprAst.LocalRef(subjName, pos), values[i], pos),
                    pos,
                )
            }
            return acc
        }

        private var tempCounter = 0
        private fun freshTempName(prefix: String): String = "${prefix}_${tempCounter++}"
    }

    // ===== 工具函数 =====

    /** 提取顶层 delay(ms) 调用的延迟表达式；非 delay 返回 null。 */
    private fun extractTopLevelDelay(s: StmtAst): ExprAst? {
        if (s !is StmtAst.ExprStmt) return null
        val call = s.call
        if (call !is ExprAst.BuiltinCallExpr) return null
        if (call.name != "delay") return null
        if (call.args.size != 1) return null
        return call.args[0]
    }

    /** 判断语句是否为 delay() 调用。 */
    private fun isDelayCall(s: StmtAst): Boolean = extractTopLevelDelay(s) != null

    private data class AwaitInfo(
        val netRequest: ExprAst.NetRequestCall?,
        /**
         * suspend 路径表达式：CallExpr（{@code await f(args)}）或 MemberCall
         * （{@code await target.method(args)}，任务 3 通用 await 引入）。
         */
        val suspendCall: ExprAst?,
        val bindName: String?,
        val tailExpr: ExprAst?,
    )

    private fun extractTopLevelAwait(s: StmtAst): AwaitInfo? {
        if (s is StmtAst.ExprStmt && s.call is ExprAst.AwaitExpr) {
            return buildAwaitInfo(s.call, bindName = null)
        }
        if (s is StmtAst.VarDeclStmt && s.initializer is ExprAst.AwaitExpr) {
            return buildAwaitInfo(s.initializer, bindName = s.name)
        }
        return null
    }

    /**
     * 把顶层 [ExprAst.AwaitExpr] 拆为 [AwaitInfo]：
     * - net.request 形态：携 netRequestCall + tailExpr
     * - suspend lambda 形态：携 suspendCall（CallExpr / MemberCall），tailExpr 强制为 null
     */
    private fun buildAwaitInfo(a: ExprAst.AwaitExpr, bindName: String?): AwaitInfo {
        if (a.suspendCall != null) {
            return AwaitInfo(
                netRequest = null,
                suspendCall = a.suspendCall,
                bindName = bindName,
                tailExpr = null,
            )
        }
        val net = a.netRequestCall as? ExprAst.NetRequestCall
            ?: error("AwaitExpr 无 netRequestCall 也无 suspendCall（AST 互斥校验失效或 lower 未完成）")
        return AwaitInfo(net, suspendCall = null, bindName = bindName, tailExpr = a.tailExpr)
    }

    /** 判断语句子树（顶层位置之外）是否嵌入了 AwaitExpr —— 这些位置当前 lowerer 不支持。 */
    private fun hasAwaitInUnsupportedPosition(s: StmtAst): Boolean {
        // 顶层 ExprStmt/VarDeclStmt 的 await 由 extractTopLevelAwait 接走，不算"嵌入"。
        if (s is StmtAst.ExprStmt && s.call is ExprAst.AwaitExpr) return false
        if (s is StmtAst.VarDeclStmt && s.initializer is ExprAst.AwaitExpr) return false
        return containsAwait(s)
    }

    /**
     * 判断语句子树（递归）是否包含 AwaitExpr。
     *
     * <p>本文件存在三个相似的 walker（[containsAwait] / [containsAwaitExpr] /
     * [containsControlEscape]），它们形似但下钻规则不同——分别关注 await、表达式内
     * await、控制流转义；其中 [containsControlEscape] 还有意"不下钻 while/for-in/try"。
     * 抽公共 walker 框架会让"是否下钻 X 子树"变成参数化条件，反而增加心智负担——故
     * 保持三个独立 when 判别。</p>
     */
    private fun containsAwait(s: StmtAst): Boolean = when (s) {
        is StmtAst.ExprStmt -> isDelayCall(s) || containsAwaitExpr(s.call)
        is StmtAst.VarDeclStmt -> containsAwaitExpr(s.initializer)
        is StmtAst.AssignStmt -> containsAwaitExpr(s.rhs)
        is StmtAst.IfStmt -> containsAwaitExpr(s.cond) ||
                s.thenBody.any { containsAwait(it) } ||
                s.elseBody.any { containsAwait(it) }
        is StmtAst.WhileStmt -> containsAwaitExpr(s.cond) || s.body.any { containsAwait(it) }
        is StmtAst.ForInStmt -> containsAwaitExpr(s.iterable) || s.body.any { containsAwait(it) }
        is StmtAst.TryStmt -> s.tryBody.any { containsAwait(it) } ||
                s.catchBody.any { containsAwait(it) } ||
                s.finallyBody.any { containsAwait(it) }
        is StmtAst.WhenStmt -> {
            val w = s.whenExpr
            containsAwaitExpr(w.subject) ||
                    w.branches.any { br ->
                        br.values.any { containsAwaitExpr(it) } || br.body.any { containsAwait(it) }
                    } ||
                    (w.elseBody?.any { containsAwait(it) } ?: false)
        }
        is StmtAst.ReturnStmt -> s.value?.let { containsAwaitExpr(it) } ?: false
        else -> false
    }

    private fun containsAwaitAnywhere(stmts: List<StmtAst>): Boolean =
        stmts.any { containsAwait(it) }

    /**
     * 顶层语句子树中是否含跨循环边界的 break/continue/return（直接位于此层 /
     * 嵌套 if 体内 / when 分支体内）；嵌套 while/for-in 内部的 break/continue 由该
     * 子循环消化，不需要外层关心，故遇内部循环时**不下钻**。
     *
     * TryStmt：D2-c 不支持 try 内 await（parser E601 硬拒），且 try 体当前不会被
     * lowerer 切状态分发；try 内的 break/continue/return 由 runtime executeTry +
     * finally 链处理（ReturnSignal 由 fun 边界 wrap 吞）。因此外层不应把含 try
     * 的语句强制切状态——**不下钻 TryStmt**。
     */
    private fun containsControlEscape(s: StmtAst): Boolean = when (s) {
        is StmtAst.BreakStmt, is StmtAst.ContinueStmt, is StmtAst.ReturnStmt -> true
        is StmtAst.IfStmt -> s.thenBody.any { containsControlEscape(it) } ||
                s.elseBody.any { containsControlEscape(it) }
        is StmtAst.WhenStmt -> {
            val w = s.whenExpr
            w.branches.any { br -> br.body.any { containsControlEscape(it) } } ||
                    (w.elseBody?.any { containsControlEscape(it) } ?: false)
        }
        // while / for-in：自身是循环边界，break/continue 已被它消化
        // TryStmt：runtime 自有 executeTry 处理 break/continue/return，无需切状态
        // FunDeclStmt：嵌套 fun 走自身 lowerer，外层不关心
        else -> false
    }

    private fun containsAwaitExpr(e: ExprAst): Boolean = when (e) {
        is ExprAst.AwaitExpr -> true
        is ExprAst.BinaryOp -> containsAwaitExpr(e.left) || containsAwaitExpr(e.right)
        is ExprAst.UnaryOp -> containsAwaitExpr(e.operand)
        is ExprAst.MemberAccess -> containsAwaitExpr(e.target)
        is ExprAst.SafeMemberAccess -> containsAwaitExpr(e.target)
        is ExprAst.MemberCall -> containsAwaitExpr(e.target) || e.args.any { containsAwaitExpr(it) }
                || e.namedArgs.any { containsAwaitExpr(it.value) }
        is ExprAst.SafeMemberCall -> containsAwaitExpr(e.target) || e.args.any { containsAwaitExpr(it) }
        is ExprAst.Elvis -> containsAwaitExpr(e.left) || containsAwaitExpr(e.right)
        is ExprAst.Ternary -> containsAwaitExpr(e.cond) ||
                containsAwaitExpr(e.thenExpr) ||
                containsAwaitExpr(e.elseExpr)
        is ExprAst.IfExpr -> containsAwaitExpr(e.cond) ||
                e.thenBody.any { containsAwait(it) } ||
                e.elseBody.any { containsAwait(it) }
        is ExprAst.Interp -> e.parts.any {
            it is ExprAst.Interp.InterpPart.ExprPart && containsAwaitExpr(it.expr)
        }
        is ExprAst.ListLiteralExpr -> e.elements.any { containsAwaitExpr(it) }
        is ExprAst.RangeExpr -> containsAwaitExpr(e.start) || containsAwaitExpr(e.endValue)
        is ExprAst.IndexExpr -> containsAwaitExpr(e.target) || containsAwaitExpr(e.index)
        is ExprAst.CallExpr -> containsAwaitExpr(e.callee) || e.args.any { containsAwaitExpr(it) }
        is ExprAst.BuiltinCallExpr -> e.args.any { containsAwaitExpr(it) }
        is ExprAst.NetRequestCall -> e.payload.any { containsAwaitExpr(it.value) } ||
                (e.callback?.let { containsAwaitExpr(it) } ?: false) ||
                (e.timeoutMs?.let { containsAwaitExpr(it) } ?: false)
        is ExprAst.LambdaExpr -> false // lambda 体独立 lowering
        else -> false
    }

    /** 把编译期 [IrType] 映射为 wire ordinal（与 dsl-ir 端 IrType 枚举一致）。 */
    fun typeOrdinal(t: IrType): Int = when (t) {
        IrType.NumberT -> 0
        IrType.StringT -> 1
        IrType.BooleanT -> 2
        // ListT / MapT 升级为 data class 后必须用 is 形式判定；wire ordinal 仍按基座降级，
        // 不携带泛型参数（dsl-ir IrType.java 是稳定枚举，不可扩展）。
        is IrType.MapT -> 3
        is IrType.ListT -> 4
        IrType.AnyT -> 5
        // M5-c：UnitT 尚未进入 wire 协议；状态机槽位无返回值的语义不出现 UnitT，
        // 这里退化为 AnyT 以保持向后兼容。
        IrType.UnitT -> 5
        is IrType.DataInstance -> 5  // 与 AnyT 同 ordinal——wire 协议不感知 data 类
        is IrType.Nullable -> 6
    }
}
